import React, { Fragment, useState } from "react";
import {
  Button,
  Pagination,
  PaginationFirst,
  PaginationLast,
  PaginationNext,
  PaginationPrevious,
  PaginationItems,
} from "components/ui";
import { ArrowUpTrayIcon, CheckIcon, XMarkIcon, MagnifyingGlassIcon, UserGroupIcon, UserIcon, ClockIcon } from "@heroicons/react/24/outline";
import { XMarkIcon as XMarkIconSolid } from "@heroicons/react/20/solid";
import { useDebounceCallback } from "hooks";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { getAllTeams } from "services/teamsService";
import { getTeamsByTeamTypeCode, getUsersForTeamType } from "services/teamsService";
import { getAllCounselors } from "services/userService";
import { assignLeadDocInspectors, verifyOrRejectLeadDocument, getTeamAssignedToDocVerification, finalApproveLeadDoc } from "services/leadsService";
import { toast } from "sonner";

import { useDocuments } from "./hooks/useDocuments";
import DocumentsFilter from "./components/DocumentsFilter";
import {
  CentralizedUpload,
  NoResultsFound,
  LoadingState,
} from "./components/DocumentEmptyStates";
import DocumentsList from "./components/DocumentsList";
import UploadDocumentModal from "./components/UploadDocumentModal";
import DocumentVerificationModal from "./components/DocumentVerificationModal";
import { hasPermission } from "utils/permissionsUtils";

import { useDocumentContext } from "app/contexts/DocumentContext/context";
const DocumentsTab = ({ isMobile, leadId, refreshProgressJourney, IsFinalApproved = false, afterFinalApprove = () => { }, isStudent = false }) => {
  const {
    documents,
    loading,
    totalRecords,
    hasInitialData,
    hasActiveFilters,
    filterState,
    handleFilterChange,
    handlePageChange,
    clearFilters,
    switchView,
    viewType,
    totalPages,
    refetch,
  } = useDocuments(leadId);

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isAssignTeamModalOpen, setIsAssignTeamModalOpen] = useState(false);
  const [assignmentType, setAssignmentType] = useState("teams"); // "teams" or "counselors"
  const [teams, setTeams] = useState([]);
  const [selectedTeamIds, setSelectedTeamIds] = useState([]);
  const [isLoadingTeams, setIsLoadingTeams] = useState(false);
  const [counselors, setCounselors] = useState([]);
  const [selectedCounselorIds, setSelectedCounselorIds] = useState([]);
  const [isLoadingCounselors, setIsLoadingCounselors] = useState(false);
  const [isAssigningDocuments, setIsAssigningDocuments] = useState(false);
  const [totalTeams, setTotalTeams] = useState(0);
  const [assignmentSearch, setAssignmentSearch] = useState("");
  const [isFinalApproving, setIsFinalApproving] = useState(false);

  // Check if all documents are approved
  const areAllDocumentsApproved = () => {
    if (!documents || documents.length === 0) {
      return false;
    }

    // Check if all documents have status 'Approved' (handle both string and numeric values)
    return documents.every(doc => {
      const status = doc.status || doc.Status || doc.verificationStatus || doc.VerificationStatus;
      return status === 'Approved' ||
        status === 'Approved' ||
        status === 1 || // Numeric status for approved
        status === '1'; // String status for approved
    });
  };

  const { refreshCounts } = useDocumentContext();
  const [isVerificationModalOpen, setIsVerificationModalOpen] = useState(false);
  const [verificationAction, setVerificationAction] = useState("approve");
  const [verificationDocument, setVerificationDocument] = useState(null);
  const [isUpdatingVerification, setIsUpdatingVerification] = useState(false);
  const debouncedTeamSearch = useDebounceCallback((value) => {
    // Note: Search is now handled client-side since the new API doesn't support search parameter
    // No API call needed here
  }, 300);

  // Note: Counselor API doesn't support search, so we filter client-side
  // No debounced search needed for counselors

  const debouncedSearch = useDebounceCallback((value) => {
    handleFilterChange("search", value);
  }, 300);

  // Function to switch views
  const switchToDocumentsView = () => {
    switchView("documents");
  };

  const switchToHistoryView = () => {
    switchView("history");
  };

  const fetchTeams = async () => {
    setIsLoadingTeams(true);
    try {
      const result = await getTeamsByTeamTypeCode("DOCV");
      if (result.success) {
        setTeams(result.data || []);
        setTotalTeams(result.totalRecords || (result.data?.length ?? 0));
      } else {
        toast.error("Failed to load teams");
        setTeams([]);
        setTotalTeams(0);
      }
    } catch (error) {
      console.error("Error fetching teams:", error);
      toast.error("Error loading teams");
      setTeams([]);
      setTotalTeams(0);
    } finally {
      setIsLoadingTeams(false);
    }
  };

  const fetchCounselors = async () => {
    setIsLoadingCounselors(true);
    try {
      const result = await getUsersForTeamType("DOCV");
      if (result.success) {
        // Map the API response to match the expected format
        const mappedCounselors = (result.data || []).map(user => ({
          userId: user.UserId,
          fullName: user.FullName,
          email: user.UserName
        }));
        setCounselors(mappedCounselors);
      } else {
        toast.error("Failed to load counselors");
        setCounselors([]);
      }
    } catch (error) {
      console.error("Error fetching counselors:", error);
      toast.error("Error loading counselors");
      setCounselors([]);
    } finally {
      setIsLoadingCounselors(false);
    }
  };

  const fetchAssignedInspectors = async () => {
    try {
      const result = await getTeamAssignedToDocVerification(leadId);
      if (result.success) {
        const assignedData = result.data || [];

        // Extract team IDs and counselor IDs from assigned data
        const assignedTeamIds = [];
        const assignedCounselorIds = [];

        assignedData.forEach(item => {
          if (item.TeamId) {
            assignedTeamIds.push(item.TeamId);
          }
          if (item.CounsellorId) {
            assignedCounselorIds.push(item.CounsellorId);
          }
        });

        setSelectedTeamIds(assignedTeamIds);
        setSelectedCounselorIds(assignedCounselorIds);

        // Set assignment type based on what's assigned
        if (assignedTeamIds.length > 0 && assignedCounselorIds.length === 0) {
          setAssignmentType("teams");
        } else if (assignedCounselorIds.length > 0 && assignedTeamIds.length === 0) {
          setAssignmentType("counselors");
        } else if (assignedTeamIds.length > 0 && assignedCounselorIds.length > 0) {
          // If both are assigned, default to teams
          setAssignmentType("teams");
        }
      }
    } catch (error) {
      console.error("Error fetching assigned inspectors:", error);
      // Don't show error toast on load, just log it
    }
  };

  const handleOpenVerificationModal = (document, action) => {
    setVerificationDocument(document);
    setVerificationAction(action);
    setIsVerificationModalOpen(true);
  };

  const handleCloseVerificationModal = () => {
    if (isUpdatingVerification) return;
    setIsVerificationModalOpen(false);
    setVerificationDocument(null);
    setVerificationAction("approve");
  };
  const handleSubmitVerification = async ({ status, remark }) => {
    if (!verificationDocument?.id) {
      toast.error("Document not found for verification");
      return;
    }

    setIsUpdatingVerification(true);
    try {
      const result = await verifyOrRejectLeadDocument({
        id: verificationDocument.id,
        verificationStatus: status,
        remarks: remark?.trim() || undefined,
      });

      if (result.success) {
        toast.success(result.message || "Verification status updated successfully.");
        setIsVerificationModalOpen(false);
        setVerificationDocument(null);
        setVerificationAction("approve");
        await refetch();
        if (typeof refreshProgressJourney === "function") {
          await refreshProgressJourney();
        }
        //Call context API here
        if (typeof refreshCounts === "function") {
          await refreshCounts();
        }
      } else {
        toast.error(result.error || "Failed to update verification status");
      }
    } catch (error) {
      console.error("Verification update error:", error);
      toast.error(error?.message || "Failed to update verification status");
    } finally {
      setIsUpdatingVerification(false);
    }
  };

  const handleOpenAssignTeamModal = () => {
    setAssignmentSearch("");
    setIsAssignTeamModalOpen(true);
    fetchTeams();
    fetchCounselors(); // Fetch all counselors once, filter client-side
    fetchAssignedInspectors(); // Fetch already assigned teams/counselors
  };

  const handleCloseAssignTeamModal = () => {
    setIsAssignTeamModalOpen(false);
    setSelectedTeamIds([]);
    setSelectedCounselorIds([]);
    setAssignmentSearch("");
  };

  const handleAssignmentTypeChange = (type) => {
    setAssignmentType(type);
    setAssignmentSearch("");
    // Keep selections when switching between assignment types
    // This preserves selections if user switches back
  };

  const toggleTeamSelection = (teamId) => {
    const isSelected = selectedTeamIds.includes(teamId);
    setSelectedTeamIds(
      isSelected
        ? selectedTeamIds.filter((id) => id !== teamId)
        : [...selectedTeamIds, teamId]
    );
  };

  const removeTeam = (teamId) => {
    setSelectedTeamIds(selectedTeamIds.filter((id) => id !== teamId));
  };

  const clearAllTeams = () => {
    setSelectedTeamIds([]);
  };

  const getTeamName = (teamId) => {
    const team = teams.find((t) => t.TeamId === teamId);
    return team?.TeamName || "Unknown Team";
  };


  const getFilteredTeams = () => {
    if (!assignmentSearch.trim()) return teams;
    const query = assignmentSearch.toLowerCase();
    return teams.filter(
      (team) =>
        team.TeamName?.toLowerCase().includes(query) ||
        team.Description?.toLowerCase().includes(query)
    );
  };


  const toggleCounselorSelection = (counselorId) => {
    const isSelected = selectedCounselorIds.includes(counselorId);
    setSelectedCounselorIds(
      isSelected
        ? selectedCounselorIds.filter((id) => id !== counselorId)
        : [...selectedCounselorIds, counselorId]
    );
  };

  const removeCounselor = (counselorId) => {
    setSelectedCounselorIds(selectedCounselorIds.filter((id) => id !== counselorId));
  };

  const clearAllCounselors = () => {
    setSelectedCounselorIds([]);
  };

  const getCounselorName = (counselorId) => {
    const counselor = counselors.find((c) => c.userId === counselorId);
    return counselor?.fullName || counselorId;
  };

  const getFilteredCounselors = () => {
    if (!assignmentSearch.trim()) return counselors;
    const query = assignmentSearch.toLowerCase();
    return counselors.filter(
      (counselor) =>
        counselor.fullName?.toLowerCase().includes(query) ||
        counselor.email?.toLowerCase().includes(query)
    );
  };

  const handleAssignTeam = async () => {
    if (assignmentType === "teams" && selectedTeamIds.length === 0) {
      toast.error("Please select at least one team");
      return;
    }

    if (assignmentType === "counselors" && selectedCounselorIds.length === 0) {
      toast.error("Please select at least one counselor");
      return;
    }

    setIsAssigningDocuments(true);

    const result = await assignLeadDocInspectors({
      leadId,
      teamIds: assignmentType === "teams" ? selectedTeamIds : [],
      counsellorIds: assignmentType === "counselors" ? selectedCounselorIds : [],
    });

    if (result.success) {
      toast.success(result.message || "Lead document inspectors assigned successfully.");
      await refetch();
      handleCloseAssignTeamModal();
    } else {
      toast.error(result.error || "Failed to assign document inspectors.");
    }

    setIsAssigningDocuments(false);
  };

  const handleFinalApprove = async () => {
    setIsFinalApproving(true);
    try {
      const result = await finalApproveLeadDoc(leadId);

      if (result.success) {
        toast.success(result.message || "Documents final approved successfully!");
        afterFinalApprove();
        await refetch();
        if (typeof refreshProgressJourney === "function") {
          await refreshProgressJourney();
        }
      } else {
        // Handle the specific error response from the API
        toast.error(result.message || "Failed to final approve documents");
      }
    } catch (error) {
      console.error("Final approve error:", error);
      toast.error(error?.message || "Failed to final approve documents");
    } finally {
      setIsFinalApproving(false);
    }
  };

  const handleUpload = () => {
    setIsUploadModalOpen(true);
  };

  const handleUploadSuccess = (uploadedFiles) => {
    // Refresh the documents list after successful upload
    refetch();
  };

  return (
    <div className="space-y-4">
      {/* Header with Upload Button */}
      {/* {(hasInitialData || hasActiveFilters) && ( */}
      <div className="bg-white dark:bg-dark-800 rounded-lg border border-gray-200 dark:border-dark-600 p-4 shadow-sm">
        {/* Title and Toggle Section */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex w-full items-center justify-between  flex-wrap">
            <div>
              <h2 className="dark:text-dark-50 text-xl font-semibold text-gray-900 mb-1">
                {viewType === "documents" ? "Documents" : "Document History"}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {viewType === "documents"
                  ? `Manage and verify lead documents (${documents.length} items)`
                  : `View document verification history (${documents.length} items)`
                }
              </p>
            </div>

            {/* View Toggle Buttons */}
            <div className="flex rounded-lg bg-gray-100 p-0.5 dark:bg-gray-700 shadow-inner">
              <button
                onClick={switchToDocumentsView}
                className={`rounded-md px-4 py-2 text-sm font-medium transition-all duration-200 ${viewType === "documents"
                  ? "bg-white text-blue-600 shadow-sm dark:bg-dark-600 dark:text-blue-400"
                  : "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
                  }`}
              >
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${viewType === "documents" ? "bg-blue-500" : "bg-transparent"
                    }`} />
                  Documents
                </div>
              </button>
              <button
                onClick={switchToHistoryView}
                className={`rounded-md px-4 py-2 text-sm font-medium transition-all duration-200 ${viewType === "history"
                  ? "bg-white text-blue-600 shadow-sm dark:bg-dark-600 dark:text-blue-400"
                  : "text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
                  }`}
              >
                <div className="flex items-center gap-2">
                  <ClockIcon className="h-4 w-4" />
                  <div className={`w-2 h-2 rounded-full ${viewType === "history" ? "bg-blue-500" : "bg-transparent"
                    }`} />
                  History
                </div>
              </button>
            </div>
          </div>
        </div>

        {viewType === "documents" && !IsFinalApproved && (
          <div className="border-t border-gray-200 dark:border-dark-600 pt-4 mt-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
              {hasPermission("MLLE") && (
                <Button
                  variant="soft"
                  color="primary"
                  onClick={handleUpload}
                  disabled={isStudent}
                  className={`w-full sm:w-auto shadow-sm hover:shadow-md transition-shadow duration-200 ${isStudent ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  <ArrowUpTrayIcon className="mr-2 h-4 w-4" />
                  <span className="font-medium">Upload Document</span>
                </Button>
              )}
              {/* Assign Team Button - Only show when there's at least one document uploaded */}
              {documents && documents.length > 0 && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleOpenAssignTeamModal}
                  disabled={isStudent}
                  className={`w-full sm:w-auto shadow-sm hover:shadow-md transition-shadow duration-200 border-gray-300 hover:border-blue-400 dark:border-dark-500 dark:hover:border-blue-400 ${isStudent ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  <UserGroupIcon className="mr-2 h-4 w-4" />
                  <span className="font-medium">Assign Team</span>
                </Button>
              )}
              {/* Final Approve Button - Only show when all documents are approved */}
              {/* {areAllDocumentsApproved() && (
                <Button
                  variant="soft"
                  color="success"
                  onClick={handleFinalApprove}
                  disabled={isFinalApproving}
                  className="w-full sm:w-auto shadow-sm hover:shadow-md transition-shadow duration-200"
                >
                  <CheckIcon className="mr-2 h-4 w-4" />
                  <span className="font-medium">
                    {isFinalApproving ? "Final Approving..." : "Final Approve"}
                  </span>
                </Button>
              )} */}
            </div>
          </div>
        )}
      </div>
      {/* )} */}

      {/* Empty state for no initial documents */}
      {/* {!hasInitialData && !hasActiveFilters && !loading && (
        <CentralizedUpload isMobile={isMobile} onUpload={handleUpload} />
      )} */}

      {/* Empty state for History view */}
      {/* {viewType === "history" && !hasInitialData && !hasActiveFilters && !loading && (
        <div className="bg-white dark:bg-dark-800 rounded-lg border border-gray-200 dark:border-dark-600 p-12 text-center shadow-sm">
          <div className="mx-auto max-w-md">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 mb-4">
              <ClockIcon className="h-8 w-8 text-gray-400 dark:text-gray-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              No History Available
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Document verification history will appear here once documents are uploaded and verification actions are performed.
            </p>
            <Button
              variant="soft"
              color="primary"
              onClick={switchToDocumentsView}
              className="mx-auto"
            >
              Go to Documents
            </Button>
          </div>
        </div>
      )} */}
      {/* Main content area */}
      {(hasInitialData || hasActiveFilters) && (
        <>
          <DocumentsFilter
            filterState={filterState}
            onFilterChange={handleFilterChange}
            onClearFilters={clearFilters}
            onSearch={debouncedSearch}
          />

          {loading && <LoadingState />}

          {!loading &&
            documents.length === 0 &&
            hasActiveFilters && (
              <NoResultsFound isMobile={isMobile} />
            )}


          {!loading &&
            documents.length > 0 && (
              <>
                <DocumentsList
                  documents={documents}
                  isMobile={isMobile}
                  refreshProgressJourney={refreshProgressJourney}
                  refetch={refetch}
                  viewType={viewType}
                  IsFinalApproved={IsFinalApproved}
                  onVerificationAction={viewType === "documents" ? handleOpenVerificationModal : null}
                  isStudent={isStudent}
                />

                {totalPages > 1 && (
                  <div className="mt-6 flex justify-center">
                    <Pagination
                      total={totalPages}
                      value={filterState.pageNumber}
                      onChange={handlePageChange}
                      className="pagination-primary"
                    >
                      <PaginationFirst className="pagination-first" />
                      <PaginationPrevious className="pagination-previous" />
                      <PaginationItems className="pagination-items" />
                      <PaginationNext className="pagination-next" />
                      <PaginationLast className="pagination-last" />
                    </Pagination>
                  </div>
                )}
              </>
            )}
        </>
      )}

      {/* Empty state for History view - Show when no history documents exist */}
      {viewType === "history" && documents.length === 0 && !loading && !hasActiveFilters && (
        <div className="bg-white dark:bg-dark-800 rounded-lg border border-gray-200 dark:border-dark-600 p-12 text-center shadow-sm">
          <div className="mx-auto max-w-md">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-700 mb-4">
              <ClockIcon className="h-8 w-8 text-gray-400 dark:text-gray-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              No History Available
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Document verification history will appear here once documents are uploaded and verification actions are performed.
            </p>
            <Button
              variant="soft"
              color="primary"
              onClick={switchToDocumentsView}
              className="mx-auto"
            >
              Go to Documents
            </Button>
          </div>
        </div>
      )}


      {/* Upload Document Modal */}
      <UploadDocumentModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUploadSuccess={handleUploadSuccess}
        refreshProgressJourney={refreshProgressJourney}
        leadId={leadId}
      />

      <DocumentVerificationModal
        isOpen={isVerificationModalOpen}
        onClose={handleCloseVerificationModal}
        document={verificationDocument}
        action={verificationAction}
        onSubmit={handleSubmitVerification}
        isSubmitting={isUpdatingVerification}
      />

      {/* Assign Verification Modal - Teams & Counselors */}
      <Transition appear show={isAssignTeamModalOpen} as={Fragment}>
        <Dialog
          as="div"
          onClose={() => { }}
          className="fixed inset-0 z-[120] flex flex-col items-center justify-center overflow-hidden px-4 py-6 sm:px-5"
        >
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="absolute inset-0 bg-gray-900/50 backdrop-blur dark:bg-black/30" />
          </TransitionChild>

          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <DialogPanel className="dark:bg-dark-700 relative flex w-full max-w-lg origin-top flex-col overflow-hidden rounded-lg bg-white transition-all duration-300 outline-none focus:outline-none">
              <div className="dark:bg-dark-800 flex items-center justify-between bg-gray-100 px-4 py-3 sm:px-5">
                <DialogTitle as="h3" className="dark:text-dark-50 text-base font-medium text-gray-900">
                  Assign Verification
                </DialogTitle>
                <Button
                  onClick={handleCloseAssignTeamModal}
                  variant="flat"
                  isIcon
                  className="size-7 rounded-full ltr:-mr-1.5 rtl:-ml-1.5"
                >
                  <XMarkIcon className="size-4.5" />
                </Button>
              </div>

              <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-5">
                <div className="mb-4">
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Assign documents to verification teams or individual counselors.
                  </p>
                </div>

                {/* Assignment Type Selection */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <button
                    type="button"
                    onClick={() => handleAssignmentTypeChange("teams")}
                    className={`relative flex items-center gap-3 rounded-lg border-2 px-4 py-3 text-left text-sm font-medium transition-all ${assignmentType === "teams"
                      ? "border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 shadow-sm"
                      : "border-gray-300 bg-white text-gray-700 hover:border-gray-400 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                      }`}
                  >
                    <div className={`flex-shrink-0 flex items-center justify-center w-5 h-5 rounded-full border-2 ${assignmentType === "teams"
                      ? "border-primary-500 bg-white dark:bg-gray-900"
                      : "border-gray-300 dark:border-gray-600"
                      }`}>
                      {assignmentType === "teams" && (
                        <div className="w-2.5 h-2.5 rounded-full bg-primary-500"></div>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <UserGroupIcon className="h-5 w-5" />
                        <span className="font-semibold">Teams</span>
                      </div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleAssignmentTypeChange("counselors")}
                    className={`relative flex items-center gap-3 rounded-lg border-2 px-4 py-3 text-left text-sm font-medium transition-all ${assignmentType === "counselors"
                      ? "border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 shadow-sm"
                      : "border-gray-300 bg-white text-gray-700 hover:border-gray-400 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                      }`}
                  >
                    <div className={`flex-shrink-0 flex items-center justify-center w-5 h-5 rounded-full border-2 ${assignmentType === "counselors"
                      ? "border-primary-500 bg-white dark:bg-gray-900"
                      : "border-gray-300 dark:border-gray-600"
                      }`}>
                      {assignmentType === "counselors" && (
                        <div className="w-2.5 h-2.5 rounded-full bg-primary-500"></div>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <UserIcon className="h-5 w-5" />
                        <span className="font-semibold">Counselors</span>
                      </div>
                    </div>
                  </button>
                </div>

                {/* Selected Items Chips - Teams */}
                {assignmentType === "teams" && selectedTeamIds.length > 0 && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                        Selected Teams ({selectedTeamIds.length})
                      </p>
                      <button
                        type="button"
                        onClick={clearAllTeams}
                        className="text-xs font-medium text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                      >
                        Clear All
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {selectedTeamIds.map((teamId) => (
                        <div
                          key={teamId}
                          className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1.5 text-sm font-medium text-primary-700 dark:bg-primary-900/30 dark:text-primary-300"
                        >
                          <UserGroupIcon className="h-4 w-4" />
                          <span>{getTeamName(teamId)}</span>
                          <button
                            type="button"
                            onClick={() => removeTeam(teamId)}
                            className="ml-1 rounded-full p-0.5 hover:bg-primary-200 dark:hover:bg-primary-800/50 transition-colors"
                            title="Remove team"
                          >
                            <XMarkIconSolid className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Selected Items Chips - Counselors */}
                {assignmentType === "counselors" && selectedCounselorIds.length > 0 && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                        Selected Counselors ({selectedCounselorIds.length})
                      </p>
                      <button
                        type="button"
                        onClick={clearAllCounselors}
                        className="text-xs font-medium text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                      >
                        Clear All
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {selectedCounselorIds.map((counselorId) => (
                        <div
                          key={counselorId}
                          className="inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1.5 text-sm font-medium text-primary-700 dark:bg-primary-900/30 dark:text-primary-300"
                        >
                          <UserIcon className="h-4 w-4" />
                          <span>{getCounselorName(counselorId)}</span>
                          <button
                            type="button"
                            onClick={() => removeCounselor(counselorId)}
                            className="ml-1 rounded-full p-0.5 hover:bg-primary-200 dark:hover:bg-primary-800/50 transition-colors"
                            title="Remove counselor"
                          >
                            <XMarkIconSolid className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Search Input */}
                <div className="mb-4">
                  <div className="relative">
                    <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder={assignmentType === "teams" ? "Search teams..." : "Search counselors..."}
                      value={assignmentSearch}
                      onChange={(e) => {
                        const { value } = e.target;
                        setAssignmentSearch(value);
                        if (assignmentType === "teams") {
                          debouncedTeamSearch(value);
                        }
                        // Counselors are filtered client-side, no API call needed
                      }}
                      className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 placeholder-gray-500 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-dark-600 dark:bg-dark-700 dark:text-dark-50 dark:placeholder-dark-400 dark:focus:ring-primary-500/30"
                    />
                  </div>
                </div>

                {/* Teams List */}
                {assignmentType === "teams" && (
                  <div className="rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="max-h-60 overflow-y-auto p-2">
                      {isLoadingTeams ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary-500 border-t-transparent"></div>
                        </div>
                      ) : getFilteredTeams().length === 0 ? (
                        <div className="py-8 text-center">
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {assignmentSearch ? "No teams found" : "No teams available"}
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          {getFilteredTeams().map((team) => (
                            <label
                              key={team.TeamId}
                              className={`flex items-start gap-3 rounded-md px-3 py-2 cursor-pointer transition-colors ${selectedTeamIds.includes(team.TeamId)
                                ? "bg-primary-50 dark:bg-primary-900/20"
                                : "hover:bg-gray-50 dark:hover:bg-gray-700/50"
                                }`}
                            >
                              <input
                                type="checkbox"
                                checked={selectedTeamIds.includes(team.TeamId)}
                                onChange={() => toggleTeamSelection(team.TeamId)}
                                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-2 focus:ring-primary-500/20"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                  {team.TeamName}
                                </div>
                                {team.Description && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                    {team.Description}
                                  </div>
                                )}
                                <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                  {team.MemberCount || 0} members
                                </div>
                              </div>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}


                {/* Counselors List */}
                {assignmentType === "counselors" && (
                  <div className="rounded-lg border border-gray-200 dark:border-gray-700">
                    <div className="max-h-60 overflow-y-auto p-2">
                      {isLoadingCounselors ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary-500 border-t-transparent"></div>
                        </div>
                      ) : getFilteredCounselors().length === 0 ? (
                        <div className="py-8 text-center">
                          <p className="text-sm text-gray-500 dark:text-gray-400">
                            {assignmentSearch ? "No counselors found" : "No counselors available"}
                          </p>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          {getFilteredCounselors().map((counselor) => (
                            <label
                              key={counselor.userId}
                              className={`flex items-start gap-3 rounded-md px-3 py-2 cursor-pointer transition-colors ${selectedCounselorIds.includes(counselor.userId)
                                ? "bg-primary-50 dark:bg-primary-900/20"
                                : "hover:bg-gray-50 dark:hover:bg-gray-700/50"
                                }`}
                            >
                              <input
                                type="checkbox"
                                checked={selectedCounselorIds.includes(counselor.userId)}
                                onChange={() => toggleCounselorSelection(counselor.userId)}
                                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-2 focus:ring-primary-500/20"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                  {counselor.fullName}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                  {counselor.email}
                                </div>
                              </div>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="dark:bg-dark-800 flex justify-end gap-3 border-t border-gray-200 bg-white px-4 py-3 sm:px-5 dark:border-dark-600">
                <Button
                  onClick={handleCloseAssignTeamModal}
                  variant="outlined"
                  className="min-w-[7rem] rounded-full"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleAssignTeam}
                  color="primary"
                  disabled={
                    (assignmentType === "teams" && selectedTeamIds.length === 0) ||
                    (assignmentType === "counselors" && selectedCounselorIds.length === 0) ||
                    isAssigningDocuments
                  }
                  className="min-w-[7rem] rounded-full"
                >
                  {isAssigningDocuments ? "Assigning..." : "Assign"}
                </Button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </Dialog>
      </Transition>
    </div>
  );
};

export default DocumentsTab;
